import { useEffect, useMemo, useState } from 'react';
import { buildLogoSources, GOOGLE_FAVICON_HOST_RE } from '../../../../../../../utils/urlMetadata';
import { isValidWebsite } from './websiteShortcut';
import { getWebsitePreview, HeroImageSource } from '../../../../../../../store/investor/apiHelpers';

/** Wait this long after the last keystroke before fetching, so typing a domain
 *  character by character doesn't fire a request per letter. */
export const WEBSITE_PREVIEW_DEBOUNCE_MS = 600;

export type WebsitePreviewStatus = 'idle' | 'loading' | 'ready';

export interface WebsitePreviewState {
    status: WebsitePreviewStatus;
    /** null while loading, or when the site publishes no hero image. */
    imageUrl: string | null;
    source: HeroImageSource | null;
    /** True once the resolved hero image failed to load at runtime. */
    heroFailed: boolean;
    /** Report a runtime <img> failure for the hero image. */
    onHeroError: () => void;
    /**
     * The lookup finished and neither a usable hero image nor any real logo
     * came back — typically a mistyped or dead domain. Single source of truth
     * for the "couldn't find this website" treatment (the strip renders
     * nothing; the Website input shows its error line).
     */
    notFound: boolean;
    /** Current candidate in the logo fallback chain; undefined when exhausted. */
    logoSource: string | undefined;
    /** Report a runtime <img> failure for the current logo candidate. */
    onLogoError: () => void;
    /**
     * Report a logo <img> load, so placeholder responses can be rejected.
     * Google's favicon service never 404s — for an unknown domain it returns a
     * generic 16px globe even when 64px was requested. A tiny image from that
     * host is the placeholder, not the company's icon: advance the chain.
     */
    onLogoLoad: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

interface FetchState {
    status: WebsitePreviewStatus;
    imageUrl: string | null;
    source: HeroImageSource | null;
    /** The normalized website this state belongs to — guards a one-render
     *  staleness window where the previous site's ready state would otherwise
     *  be read against the new input (effects reset only after that render). */
    website: string;
}

const IDLE: FetchState = { status: 'idle', imageUrl: null, source: null, website: '' };

/**
 * Resolves the hero image for whatever website is currently in the Add Lead form,
 * and owns the full image fallback chain (hero → company logo → nothing), so the
 * rendering component stays purely presentational.
 *
 * Handles all three ways a website reaches that field — picked from global search,
 * auto-fetched for a new company, or typed by hand — because it only watches the
 * resolved value. Typing is debounced; the previous request is aborted whenever
 * the website changes, so a slow lookup for an old domain can't overwrite a newer one.
 *
 * `status: 'ready'` with `imageUrl: null` means "we looked, there is no hero image" —
 * the caller should fall back to `logoSource` rather than showing an empty box.
 *
 * @param website  Raw website value from the form (may be partial or invalid).
 * @param isPending True while the modal is still auto-fetching the website, so we
 *                  don't look up a half-populated or placeholder value.
 */
export function useWebsitePreview(website: string, isPending = false): WebsitePreviewState {
    const [state, setState] = useState<FetchState>(IDLE);
    // A URL can 404 or be hotlink-blocked even though the tag existed, so runtime
    // <img> failures are tracked here to step down the chain when they happen.
    const [heroFailed, setHeroFailed] = useState(false);
    const [logoIndex, setLogoIndex] = useState(0);

    const trimmed = (website || '').trim();
    const shouldFetch = !isPending && isValidWebsite(trimmed);

    const logoSources = useMemo(() => buildLogoSources(undefined, trimmed), [trimmed]);

    useEffect(() => {
        setHeroFailed(false);
    }, [state.imageUrl]);

    useEffect(() => {
        setLogoIndex(0);
    }, [logoSources]);

    useEffect(() => {
        if (!shouldFetch) {
            setState(IDLE);
            return;
        }

        setState({ status: 'loading', imageUrl: null, source: null, website: trimmed });

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            try {
                const result = await getWebsitePreview(trimmed, controller.signal);
                setState({ status: 'ready', imageUrl: result.imageUrl, source: result.source, website: trimmed });
            } catch (error) {
                if (controller.signal.aborted) return; // superseded by a newer website
                // A transport failure is indistinguishable from "no image" for the
                // user, so land on the same fallback rather than surfacing an error.
                setState({ status: 'ready', imageUrl: null, source: null, website: trimmed });
            }
        }, WEBSITE_PREVIEW_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [trimmed, shouldFetch]);

    const logoSource = logoSources[logoIndex];
    const notFound = state.status === 'ready' && state.website === trimmed && shouldFetch && (!state.imageUrl || heroFailed) && !logoSource;

    return {
        ...state,
        notFound,
        heroFailed,
        onHeroError: () => setHeroFailed(true),
        logoSource,
        onLogoError: () => setLogoIndex((index) => index + 1),
        onLogoLoad: (event) => {
            // Google's favicon service serves a generic 16px globe (never a 404)
            // when it has nothing for the domain — treat that as a failure.
            if (logoSource && GOOGLE_FAVICON_HOST_RE.test(logoSource) && event.currentTarget.naturalWidth < 32) {
                setLogoIndex((index) => index + 1);
            }
        },
    };
}
